import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Save, 
  Building, 
  Loader2, 
  Plus, 
  Trash2, 
  Copy, 
  Check,
  Store,
  Users,
  ShoppingCart,
  Calendar,
  Lock,
  Eye,
  EyeOff,
  Shield,
  User,
  Mail,
  Phone,
  KeyRound,
  LogIn,
  ArrowLeftRight,
  Download,
  Upload,
  FileJson,
  FileSpreadsheet,
  Database,
  AlertTriangle,
  HardDrive
} from 'lucide-react';

const SettingsPage = () => {
  const { API_URL, getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [bakeries, setBakeries] = useState([]);
  const [bakeriesLoading, setBakeriesLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBakery, setSelectedBakery] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [newBakeryData, setNewBakeryData] = useState({
    username: '',
    password: '',
    name: ''
  });
  const [formData, setFormData] = useState({
    company_name: '',
    owner_name: '',
    address: '',
    phone: '',
    tax_number: '',
    logo_base64: '',
    week_start_day: 'saturday'
  });
  
  // App Settings state
  const [appSettings, setAppSettings] = useState({
    production_password: ''
  });
  const [showProductionPassword, setShowProductionPassword] = useState(false);
  const [savingAppSettings, setSavingAppSettings] = useState(false);
  
  // Profile & Password state
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [enteringBakery, setEnteringBakery] = useState(null);
  
  // Backup & Restore state
  const [backupInfo, setBackupInfo] = useState(null);
  const [loadingBackupInfo, setLoadingBackupInfo] = useState(false);
  const [exportingJson, setExportingJson] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [importingBackup, setImportingBackup] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [clearExistingData, setClearExistingData] = useState(false);
  const [selectedBackupFile, setSelectedBackupFile] = useState(null);

  useEffect(() => {
    if (user?.company_info) {
      setFormData({
        company_name: user.company_info.company_name || '',
        owner_name: user.company_info.owner_name || '',
        address: user.company_info.address || '',
        phone: user.company_info.phone || '',
        tax_number: user.company_info.tax_number || '',
        logo_base64: user.company_info.logo_base64 || '',
        week_start_day: user.company_info.week_start_day || 'saturday'
      });
    }
    // Only fetch bakeries if super admin
    if (user?.is_super_admin) {
      fetchBakeries();
    }
    // Fetch app settings
    fetchAppSettings();
    // Fetch profile
    fetchProfile();
    // Fetch backup info
    fetchBackupInfo();
  }, [user]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auth/profile`, { headers: getAuthHeader() });
      setProfileData({
        name: res.data.name || '',
        email: res.data.email || '',
        phone: res.data.phone || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  // Backup & Restore functions
  const fetchBackupInfo = async () => {
    setLoadingBackupInfo(true);
    try {
      const res = await axios.get(`${API_URL}/api/backup/info`, { headers: getAuthHeader() });
      setBackupInfo(res.data);
    } catch (error) {
      console.error('Error fetching backup info:', error);
    } finally {
      setLoadingBackupInfo(false);
    }
  };

  const exportBackupJson = async () => {
    setExportingJson(true);
    try {
      const res = await axios.get(`${API_URL}/api/backup/export`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response header or generate one
      const contentDisposition = res.headers['content-disposition'];
      let filename = `backup_${new Date().toISOString().split('T')[0]}.json`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('تم تنزيل النسخة الاحتياطية بنجاح (JSON)');
    } catch (error) {
      toast.error('فشل تنزيل النسخة الاحتياطية');
      console.error('Export error:', error);
    } finally {
      setExportingJson(false);
    }
  };

  const exportBackupExcel = async () => {
    setExportingExcel(true);
    try {
      const res = await axios.get(`${API_URL}/api/backup/export-excel`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response header or generate one
      const contentDisposition = res.headers['content-disposition'];
      let filename = `backup_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('تم تنزيل النسخة الاحتياطية بنجاح (Excel)');
    } catch (error) {
      toast.error('فشل تنزيل النسخة الاحتياطية');
      console.error('Export error:', error);
    } finally {
      setExportingExcel(false);
    }
  };

  const handleBackupFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.error('يجب أن يكون الملف بصيغة JSON');
        return;
      }
      setSelectedBackupFile(file);
      setRestoreDialogOpen(true);
    }
  };

  const importBackup = async () => {
    if (!selectedBackupFile) return;
    
    setImportingBackup(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedBackupFile);
      
      const res = await axios.post(
        `${API_URL}/api/backup/import?clear_existing=${clearExistingData}`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(`تم استيراد النسخة الاحتياطية بنجاح`);
      setRestoreDialogOpen(false);
      setSelectedBackupFile(null);
      setClearExistingData(false);
      
      // Refresh backup info
      fetchBackupInfo();
      
      // Show imported counts
      const imported = res.data.imported || {};
      const totalImported = Object.values(imported).reduce((a, b) => a + b, 0);
      toast.info(`تم استيراد ${totalImported} سجل`);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'فشل استيراد النسخة الاحتياطية';
      toast.error(errorMsg);
      console.error('Import error:', error);
    } finally {
      setImportingBackup(false);
    }
  };

  const fetchAppSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/app-settings`, { headers: getAuthHeader() });
      setAppSettings({
        production_password: res.data.production_password || ''
      });
    } catch (error) {
      console.error('Error fetching app settings:', error);
    }
  };

  const handleSaveAppSettings = async () => {
    setSavingAppSettings(true);
    try {
      await axios.put(`${API_URL}/api/app-settings`, appSettings, { headers: getAuthHeader() });
      toast.success('تم حفظ إعدادات الأمان بنجاح');
    } catch (error) {
      toast.error('حدث خطأ في الحفظ');
    } finally {
      setSavingAppSettings(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/api/auth/profile`, profileData, { headers: getAuthHeader() });
      toast.success('تم تحديث البيانات الشخصية بنجاح');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ في الحفظ');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validation
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('كلمة المرور الجديدة وتأكيدها غير متطابقين');
      return;
    }
    
    if (passwordData.new_password.length < 4) {
      toast.error('كلمة المرور يجب أن تكون 4 أحرف على الأقل');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API_URL}/api/auth/change-password`, passwordData, { headers: getAuthHeader() });
      toast.success('تم تغيير كلمة المرور بنجاح');
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ في تغيير كلمة المرور');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleEnterBakery = async (bakery) => {
    setEnteringBakery(bakery.id);
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/impersonate/${bakery.id}`,
        {},
        { headers: getAuthHeader() }
      );
      
      // Store original token and impersonation info
      const originalToken = localStorage.getItem('token');
      localStorage.setItem('original_super_admin_token', originalToken);
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('impersonated_bakery_name', bakery.name);
      
      toast.success(`تم الدخول إلى مخبز: ${bakery.name}`);
      
      // Redirect to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Error entering bakery:', error);
      toast.error(error.response?.data?.detail || 'فشل الدخول إلى المخبز');
    } finally {
      setEnteringBakery(null);
    }
  };

  const fetchBakeries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/all-bakeries`, {
        headers: getAuthHeader()
      });
      setBakeries(response.data);
    } catch (error) {
      console.error('Error fetching bakeries:', error);
    } finally {
      setBakeriesLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500000) {
        toast.error('حجم الصورة يجب أن يكون أقل من 500KB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, logo_base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(
        `${API_URL}/api/auth/company-info`,
        formData,
        { headers: getAuthHeader() }
      );
      toast.success('تم حفظ معلومات الشركة بنجاح');
      // Refresh page to update user context
      window.location.reload();
    } catch (error) {
      console.error('Error saving company info:', error);
      toast.error('حدث خطأ في حفظ المعلومات');
    } finally {
      setLoading(false);
    }
  };

  const openAddBakeryDialog = () => {
    setNewBakeryData({ username: '', password: '', name: '' });
    setDialogOpen(true);
  };

  const handleCreateBakery = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API_URL}/api/auth/register-bakery`,
        {
          ...newBakeryData,
          role: 'admin'
        },
        { headers: getAuthHeader() }
      );
      toast.success('تم إنشاء حساب المخبز بنجاح');
      setDialogOpen(false);
      fetchBakeries();
    } catch (error) {
      console.error('Error creating bakery:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('حدث خطأ في إنشاء الحساب');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyCredentials = (bakery) => {
    const text = `اسم المستخدم: ${bakery.username}\nالرابط: ${window.location.origin}`;
    navigator.clipboard.writeText(text);
    setCopiedId(bakery.id);
    toast.success('تم نسخ بيانات الدخول');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const openDeleteDialog = (bakery) => {
    setSelectedBakery(bakery);
    setDeleteDialogOpen(true);
  };

  const handleDeleteBakery = async () => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/bakeries/${selectedBakery.id}`,
        { headers: getAuthHeader() }
      );
      toast.success('تم حذف المخبز بنجاح');
      setDeleteDialogOpen(false);
      fetchBakeries();
    } catch (error) {
      console.error('Error deleting bakery:', error);
      toast.error('حدث خطأ في حذف المخبز');
    }
  };

  return (
    <div data-testid="settings-page" className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-stone-900">الإعدادات</h1>
        <p className="text-stone-500 mt-1">إعدادات الشركة وإدارة المخابز</p>
      </div>

      {/* Company Info Section */}
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="w-5 h-5 text-amber-600" />
            معلومات شركتي
          </CardTitle>
          <CardDescription>
            هذه المعلومات تظهر في كشوف الحسابات والتقارير
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>اسم الشركة / المخبز</Label>
                <Input
                  data-testid="company-name-input"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="مثال: مخبز السلام"
                />
              </div>
              <div className="space-y-2">
                <Label>اسم المالك</Label>
                <Input
                  data-testid="owner-name-input"
                  value={formData.owner_name}
                  onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                  placeholder="مثال: أحمد محمد"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>العنوان</Label>
                <Input
                  data-testid="company-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="العنوان الكامل"
                />
              </div>
              <div className="space-y-2">
                <Label>رقم الهاتف</Label>
                <Input
                  data-testid="company-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+49 123 456789"
                />
              </div>
              <div className="space-y-2">
                <Label>الرقم الضريبي</Label>
                <Input
                  data-testid="tax-number-input"
                  value={formData.tax_number}
                  onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                  placeholder="DE123456789"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>شعار الشركة (اختياري)</Label>
              <div className="flex items-center gap-4">
                {formData.logo_base64 && (
                  <img
                    src={formData.logo_base64}
                    alt="Logo"
                    className="w-20 h-20 object-contain border border-stone-200 rounded-lg bg-white"
                  />
                )}
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                  />
                  <p className="text-xs text-stone-500 mt-1">الحد الأقصى 500KB - يُفضل صورة مربعة</p>
                </div>
              </div>
            </div>

            {/* Week Start Day Setting */}
            <div className="space-y-2 pt-4 border-t">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-amber-600" />
                بداية الأسبوع
              </Label>
              <Select 
                value={formData.week_start_day} 
                onValueChange={(value) => setFormData({ ...formData, week_start_day: value })}
              >
                <SelectTrigger className="w-full sm:w-64">
                  <SelectValue placeholder="اختر يوم بداية الأسبوع" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="saturday">السبت</SelectItem>
                  <SelectItem value="sunday">الأحد</SelectItem>
                  <SelectItem value="monday">الإثنين</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-500">يؤثر على حسابات الأسبوع في بوابة الزبون والتقارير</p>
            </div>

            <Button
              type="submit"
              data-testid="save-company-info-btn"
              className="bg-amber-600 hover:bg-amber-700"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Save className="w-4 h-4 me-2" />}
              حفظ المعلومات
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Personal Profile Section */}
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            البيانات الشخصية
          </CardTitle>
          <CardDescription>
            تحديث الاسم والبريد الإلكتروني ورقم الهاتف
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  الاسم
                </Label>
                <Input
                  data-testid="profile-name-input"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  placeholder="اسمك الكامل"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-600" />
                  البريد الإلكتروني
                </Label>
                <Input
                  data-testid="profile-email-input"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  placeholder="example@domain.com"
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-600" />
                  رقم الهاتف
                </Label>
                <Input
                  data-testid="profile-phone-input"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  placeholder="+49 123 456789"
                  dir="ltr"
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="save-profile-btn"
              className="bg-blue-600 hover:bg-blue-700"
              disabled={savingProfile}
            >
              {savingProfile ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Save className="w-4 h-4 me-2" />}
              حفظ البيانات الشخصية
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Change Password Section */}
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-purple-600" />
            تغيير كلمة المرور
          </CardTitle>
          <CardDescription>
            أدخل كلمة المرور القديمة والجديدة لتغيير كلمة المرور
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>كلمة المرور الحالية</Label>
                <div className="relative">
                  <Input
                    data-testid="old-password-input"
                    type={showPasswords.old ? "text" : "password"}
                    value={passwordData.old_password}
                    onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                    placeholder="كلمة المرور الحالية"
                    dir="ltr"
                    className="pe-10"
                    required
                  />
                  <button
                    type="button"
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    onClick={() => setShowPasswords({ ...showPasswords, old: !showPasswords.old })}
                  >
                    {showPasswords.old ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>كلمة المرور الجديدة</Label>
                <div className="relative">
                  <Input
                    data-testid="new-password-input"
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="كلمة المرور الجديدة"
                    dir="ltr"
                    className="pe-10"
                    required
                    minLength={4}
                  />
                  <button
                    type="button"
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>تأكيد كلمة المرور الجديدة</Label>
                <div className="relative">
                  <Input
                    data-testid="confirm-password-input"
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    placeholder="أعد إدخال كلمة المرور"
                    dir="ltr"
                    className="pe-10"
                    required
                  />
                  <button
                    type="button"
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            
            {passwordData.new_password && passwordData.confirm_password && 
             passwordData.new_password !== passwordData.confirm_password && (
              <p className="text-sm text-red-500">⚠️ كلمة المرور الجديدة وتأكيدها غير متطابقين</p>
            )}

            <Button
              type="submit"
              data-testid="change-password-btn"
              className="bg-purple-600 hover:bg-purple-700"
              disabled={changingPassword || !passwordData.old_password || !passwordData.new_password || !passwordData.confirm_password}
            >
              {changingPassword ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <KeyRound className="w-4 h-4 me-2" />}
              تغيير كلمة المرور
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-600" />
            إعدادات الأمان
          </CardTitle>
          <CardDescription>
            حماية أقسام معينة بكلمة مرور
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-600" />
              كلمة مرور قسم كميات الإنتاج
            </Label>
            <div className="relative">
              <Input
                type={showProductionPassword ? "text" : "password"}
                value={appSettings.production_password}
                onChange={(e) => setAppSettings({ ...appSettings, production_password: e.target.value })}
                placeholder="اتركها فارغة لإلغاء الحماية"
                className="pe-10"
              />
              <button
                type="button"
                className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                onClick={() => setShowProductionPassword(!showProductionPassword)}
              >
                {showProductionPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-stone-500">
              عند تعيين كلمة مرور، ستُخفى كميات الإنتاج حتى يتم إدخال كلمة المرور الصحيحة
            </p>
          </div>
          
          <Button
            onClick={handleSaveAppSettings}
            disabled={savingAppSettings}
            className="bg-red-600 hover:bg-red-700"
          >
            {savingAppSettings ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Save className="w-4 h-4 me-2" />}
            حفظ إعدادات الأمان
          </Button>
        </CardContent>
      </Card>

      {/* Backup & Restore Section */}
      <Card className="border-stone-200 border-2 border-green-100">
        <CardHeader className="bg-gradient-to-l from-green-50 to-emerald-50">
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-green-600" />
            النسخ الاحتياطي والاستعادة
          </CardTitle>
          <CardDescription>
            احفظ نسخة احتياطية من جميع بياناتك لاستعادتها لاحقاً
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          {/* Backup Info */}
          {loadingBackupInfo ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
          ) : backupInfo && (
            <div className="p-4 bg-stone-50 rounded-lg border">
              <h4 className="font-medium text-stone-700 mb-3 flex items-center gap-2">
                <Database className="w-4 h-4" />
                ملخص البيانات
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 text-sm">
                <div className="p-2 bg-white rounded border">
                  <span className="text-stone-500">المنتجات:</span>
                  <span className="font-bold text-stone-800 mr-1">{backupInfo.counts?.products || 0}</span>
                </div>
                <div className="p-2 bg-white rounded border">
                  <span className="text-stone-500">العملاء:</span>
                  <span className="font-bold text-stone-800 mr-1">{backupInfo.counts?.customers || 0}</span>
                </div>
                <div className="p-2 bg-white rounded border">
                  <span className="text-stone-500">الطلبات:</span>
                  <span className="font-bold text-stone-800 mr-1">{backupInfo.counts?.orders || 0}</span>
                </div>
                <div className="p-2 bg-white rounded border">
                  <span className="text-stone-500">المدفوعات:</span>
                  <span className="font-bold text-stone-800 mr-1">{backupInfo.counts?.payments || 0}</span>
                </div>
                <div className="p-2 bg-white rounded border">
                  <span className="text-stone-500">الفواتير:</span>
                  <span className="font-bold text-stone-800 mr-1">{backupInfo.counts?.german_invoices || 0}</span>
                </div>
                <div className="p-2 bg-white rounded border">
                  <span className="text-stone-500">الموردين:</span>
                  <span className="font-bold text-stone-800 mr-1">{backupInfo.counts?.suppliers || 0}</span>
                </div>
                <div className="p-2 bg-white rounded border">
                  <span className="text-stone-500">المصروفات:</span>
                  <span className="font-bold text-stone-800 mr-1">{backupInfo.counts?.expenses || 0}</span>
                </div>
                <div className="p-2 bg-white rounded border">
                  <span className="text-stone-500">الموظفين:</span>
                  <span className="font-bold text-stone-800 mr-1">{backupInfo.counts?.employees || 0}</span>
                </div>
              </div>
              <div className="mt-3 p-2 bg-green-100 rounded text-center">
                <span className="text-green-700 font-bold">
                  إجمالي السجلات: {backupInfo.total_records || 0}
                </span>
              </div>
            </div>
          )}

          {/* Export Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-stone-700 flex items-center gap-2">
              <Download className="w-4 h-4 text-green-600" />
              تنزيل نسخة احتياطية
            </h4>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={exportBackupJson}
                disabled={exportingJson}
                className="bg-green-600 hover:bg-green-700 gap-2"
              >
                {exportingJson ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileJson className="w-4 h-4" />
                )}
                تنزيل JSON
              </Button>
              <Button
                onClick={exportBackupExcel}
                disabled={exportingExcel}
                variant="outline"
                className="border-green-300 text-green-700 hover:bg-green-50 gap-2"
              >
                {exportingExcel ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4" />
                )}
                تنزيل Excel
              </Button>
            </div>
            <p className="text-xs text-stone-500">
              💡 ملف JSON للاستعادة الكاملة | ملف Excel للمراجعة والتصدير
            </p>
          </div>

          {/* Import Section */}
          <div className="space-y-3 pt-4 border-t">
            <h4 className="font-medium text-stone-700 flex items-center gap-2">
              <Upload className="w-4 h-4 text-blue-600" />
              استعادة من نسخة احتياطية
            </h4>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept=".json"
                onChange={handleBackupFileSelect}
                className="hidden"
                id="backup-file-input"
              />
              <label htmlFor="backup-file-input">
                <Button
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50 gap-2 cursor-pointer"
                  asChild
                >
                  <span>
                    <Upload className="w-4 h-4" />
                    اختر ملف JSON
                  </span>
                </Button>
              </label>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>تحذير:</strong> الاستعادة ستضيف البيانات من الملف. يمكنك اختيار مسح البيانات الحالية أولاً عند الاستعادة.
                </span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              استعادة النسخة الاحتياطية
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                سيتم استيراد البيانات من الملف: <strong>{selectedBackupFile?.name}</strong>
              </p>
              
              <div className="p-3 bg-stone-50 rounded-lg border">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={clearExistingData}
                    onChange={(e) => setClearExistingData(e.target.checked)}
                    className="w-4 h-4 rounded border-stone-300"
                  />
                  <span className="text-sm">
                    مسح البيانات الحالية قبل الاستيراد
                  </span>
                </label>
              </div>
              
              {clearExistingData && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>تحذير:</strong> سيتم حذف جميع بياناتك الحالية واستبدالها بالنسخة الاحتياطية!
                    </span>
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setSelectedBackupFile(null);
              setClearExistingData(false);
            }}>
              إلغاء
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={importBackup}
              disabled={importingBackup}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {importingBackup ? (
                <Loader2 className="w-4 h-4 animate-spin me-2" />
              ) : (
                <Upload className="w-4 h-4 me-2" />
              )}
              استعادة
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bakeries Management Section - Only for Super Admin */}
      {user?.is_super_admin && (
        <Card className="border-stone-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Store className="w-5 h-5 text-blue-600" />
                  إدارة المخابز
                </CardTitle>
                <CardDescription>
                  أنت المدير الرئيسي - فقط أنت تستطيع إنشاء وحذف حسابات المخابز
                </CardDescription>
              </div>
              <Button
                data-testid="add-bakery-btn"
                onClick={openAddBakeryDialog}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 me-2" />
                إضافة مخبز
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {bakeriesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
              </div>
            ) : bakeries.length === 0 ? (
              <div className="text-center py-8 text-stone-500">
              <Store className="w-12 h-12 mx-auto text-stone-300 mb-3" />
              <p>لا يوجد مخابز مسجلة</p>
              <p className="text-sm">أنشئ حساباً جديداً لمخبز آخر</p>
            </div>
          ) : (
            <div className="space-y-3">
              {bakeries.map((bakery) => (
                <div
                  key={bakery.id}
                  className="flex items-center justify-between p-4 bg-stone-50 rounded-lg border border-stone-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Store className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-stone-900">{bakery.name}</h4>
                      <p className="text-sm text-stone-500">@{bakery.username}</p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-stone-400">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {bakery.customers_count} زبون
                        </span>
                        <span className="flex items-center gap-1">
                          <ShoppingCart className="w-3 h-3" />
                          {bakery.orders_count} طلب
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Enter Bakery Button */}
                    {bakery.id !== user?.id && !bakery.is_super_admin && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleEnterBakery(bakery)}
                        disabled={enteringBakery === bakery.id}
                        className="bg-blue-600 hover:bg-blue-700 text-white gap-1"
                        title="الدخول إلى هذا المخبز"
                      >
                        {enteringBakery === bakery.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <LogIn className="w-4 h-4" />
                        )}
                        <span className="hidden sm:inline">دخول</span>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyCredentials(bakery)}
                      className="text-stone-600"
                      title="نسخ بيانات الدخول"
                    >
                      {copiedId === bakery.id ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                    {bakery.id !== user?.id && !bakery.is_super_admin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(bakery)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="حذف المخبز"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Add Bakery Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>إضافة مخبز جديد</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateBakery} className="space-y-4">
            <div className="space-y-2">
              <Label>اسم المخبز *</Label>
              <Input
                data-testid="new-bakery-name"
                value={newBakeryData.name}
                onChange={(e) => setNewBakeryData({ ...newBakeryData, name: e.target.value })}
                placeholder="مثال: مخبز النور"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>اسم المستخدم *</Label>
              <Input
                data-testid="new-bakery-username"
                value={newBakeryData.username}
                onChange={(e) => setNewBakeryData({ ...newBakeryData, username: e.target.value })}
                placeholder="مثال: noor_bakery"
                required
                dir="ltr"
              />
              <p className="text-xs text-stone-500">سيستخدم هذا الاسم لتسجيل الدخول</p>
            </div>
            <div className="space-y-2">
              <Label>كلمة المرور *</Label>
              <Input
                data-testid="new-bakery-password"
                type="password"
                value={newBakeryData.password}
                onChange={(e) => setNewBakeryData({ ...newBakeryData, password: e.target.value })}
                placeholder="كلمة مرور قوية"
                required
                dir="ltr"
              />
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
              <p className="font-bold mb-1">ملاحظة:</p>
              <p>كل مخبز سيكون له:</p>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>زبائن خاصين به</li>
                <li>منتجات خاصة به</li>
                <li>طلبات وحسابات منفصلة</li>
                <li>سائقين خاصين به</li>
              </ul>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                إلغاء
              </Button>
              <Button
                type="submit"
                data-testid="create-bakery-btn"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'إنشاء الحساب'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>هل أنت متأكد من حذف هذا المخبز؟</AlertDialogTitle>
            <AlertDialogDescription>
              سيتم حذف المخبز "{selectedBakery?.name}" وجميع بياناته (الزبائن، الطلبات، المنتجات).
              هذا الإجراء لا يمكن التراجع عنه.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBakery} className="bg-red-600 hover:bg-red-700">
              حذف المخبز
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SettingsPage;
